import { useSlack } from "@/contexts/SlackContext";
import { useAircall } from "@/contexts/AircallContext";
import { SlackMark, AircallMark } from "@/components/BrandMarks";
import { Bell, BellOff } from "lucide-react";
import { cn } from "@/lib/utils";

// Sidebar launchers for the Slack & Aircall docks (replaces the floating pills).
// The Aircall row also renders a compact bell / bell-off button so Dean can
// silence the browser iframe with one click without expanding the dock —
// Paul's Jul 24 2026 request: "3 rings, need a mute toggle in a discoverable
// spot".
export const DockLaunchers = ({ sidebarOpen }) => {
  const slack = useSlack();
  const aircall = useAircall();

  if (!aircall && !slack) return null;
  return (
    <>
      {aircall && (
        <div
          className={cn(
            "w-full flex items-center gap-1 rounded-lg transition-colors",
            aircall.expanded ? "bg-slate-100" : "hover:bg-slate-100",
          )}
        >
          <button
            type="button"
            onClick={() => (aircall.expanded ? aircall.collapse() : aircall.expand())}
            data-testid="dock-launcher-aircall"
            title={aircall.muted ? "Aircall (muted in this browser)" : "Aircall"}
            className={cn(
              "flex-1 flex items-center gap-2 px-3 py-2 text-sm rounded-lg",
              aircall.expanded ? "text-foreground font-medium" : "text-muted-foreground hover:text-foreground",
              !sidebarOpen && "justify-center",
            )}
          >
            <span className="relative flex-shrink-0">
              <AircallMark className="w-5 h-5" />
              {aircall.loggedIn && !aircall.muted && (
                <span className="absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full bg-emerald-500 ring-2 ring-white" />
              )}
              {aircall.muted && (
                <span
                  className="absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full bg-amber-500 ring-2 ring-white"
                  title="Muted in this browser"
                />
              )}
            </span>
            {sidebarOpen && (
              <span className={aircall.muted ? "text-amber-700" : ""}>
                {aircall.muted ? "Aircall (muted)" : "Aircall"}
              </span>
            )}
          </button>
          {sidebarOpen && (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); aircall.toggleMute(); }}
              data-testid="dock-launcher-aircall-mute"
              title={
                aircall.muted
                  ? "Unmute Aircall — calls will ring in this browser again"
                  : "Mute Aircall in this browser (calls will only ring in your desktop/mobile app)"
              }
              className={cn(
                "shrink-0 mr-1 p-1.5 rounded-md transition-colors",
                aircall.muted
                  ? "text-amber-600 hover:bg-amber-50 hover:text-amber-700"
                  : "text-slate-400 hover:bg-slate-200 hover:text-slate-700",
              )}
            >
              {aircall.muted ? <BellOff className="w-4 h-4" /> : <Bell className="w-4 h-4" />}
            </button>
          )}
        </div>
      )}

      {slack && (
        <button
          type="button"
          onClick={() => (slack.expanded ? slack.collapse() : slack.expand())}
          data-testid="dock-launcher-slack"
          title="Slack"
          className={cn(
            "w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors",
            slack.expanded
              ? "bg-slate-100 text-foreground font-medium"
              : "text-muted-foreground hover:bg-slate-100 hover:text-foreground",
            !sidebarOpen && "justify-center",
          )}
        >
          <span className="relative flex-shrink-0">
            <SlackMark className="w-5 h-5" />
            {slack.status?.ok && (
              <span className="absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full bg-emerald-500 ring-2 ring-white" />
            )}
          </span>
          {sidebarOpen && <span>Slack</span>}
        </button>
      )}
    </>
  );
};
